import { LicenseName, StoredLicenses } from "@medapro-types/common/common";
import { ImportType } from "@medapro-types/Imports/import-enum";
import apps from "@services/applications";
import { nextTick } from "vue";
import { createRouter, createWebHistory, RouteLocation, RouteLocationNormalizedGeneric, RouteRecordNormalized } from "vue-router";
import docExtensions from "../plugins/document-extensions";
import eventLogger from "../services/ApiLogger/EventLogger";
import store from "../store";
import routes from "./routes";
import { safeRedirect } from "../services/util";

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes
});

router.beforeEach(async (to, from) => {
  if (!store.state.meta.sessionRestoreCompleted) {
    try {
      await store.dispatch("tryRestore");
    } catch (error) {
      if (to.name !== "logout") {
        eventLogger.error("Medapro restore localstorage", "restore failed", error);
      }
    }
  }

  if (to.matched.every((x) => !x.path) || to.fullPath === "/") {
    return { name: "dashboard" };
  } else if (!store.getters.isAuthenticatedSession && to.matched.some((record) => record.meta.authenticatedRoute)) {
    if (to.matched.length > 0) return { name: "login", query: { go: to.fullPath } };
    else return { name: "login" };
  } else {
    const matched = to.matched.filter((x) => x.meta.fullReload);
    // No need to reload if clicked Sign In Again button from Logout page
    if (matched.length && from.fullPath != "/" && from.name != "logout") {
      // Replace module-restricted with dashboard (default app on login), if logged out from any restricted module
      let baseUrl = docExtensions.getBaseUrl().replace(/\/+$/, ""); // Remove trainiling slash(/) from base url, since to.fullPath has slash in front
      if (baseUrl && !baseUrl.startsWith(window.location.protocol)) baseUrl = window.location.protocol + "//" + baseUrl;
      safeRedirect(encodeURI(baseUrl + to.fullPath.replace(new RegExp("module-restricted.*"), "dashboard")));
      return;
    }

    // Since Import module has common route for all types, we are adding the application id in the meta data here w.r.to type.
    if (to.name === "create-import") {
      to.meta.application = apps.Imports[to.params.type as ImportType].Id;
    }

    if (
      store.state.user.applicationPermissions &&
      to.matched.every((x) => {
        const application: number | null | undefined = typeof x.meta.application === "function" ? x.meta.application(to) : x.meta.application;
        if (!application) return true;

        const hasPermission = store.state.user.applicationPermissions[application];
        if (!hasPermission) return false;

        const licenseCheckResult = checkLicense(to, x);

        if (licenseCheckResult?.generalLicense !== undefined) {
          if (licenseCheckResult.generalLicense) {
            if (licenseCheckResult.hasUserAssignedLicense !== undefined) return licenseCheckResult.hasUserAssignedLicense;
          } else return false;
        }

        return true;
      })
    ) {
      return true;
    } else {
      const appPath = to.matched.find((x) => x.meta.application);

      if (appPath) {
        const application = typeof appPath.meta.application === "function" ? appPath.meta.application(to) : appPath.meta.application;
        return { name: "module-restricted", query: { application: application as number } };
      }
    }
  }
});

router.afterEach(async (to, from, failure) => {
  nextTick(() => {
    updateTitle();
  });
  if (!failure && from.meta?.module != to.meta?.module) {
    if (from.meta?.module) store.dispatch("resetModuleStore", from.meta.module);
  }
});

function updateTitle() {
  const isPWA =
    window.matchMedia("(display-mode: standalone)").matches ||
    (window.navigator as unknown as { standalone?: boolean }).standalone ||
    new URLSearchParams(window.location.search).has("pwa");
  const titleRoute = router?.currentRoute.value?.matched?.find((x) => !!x.meta.title);
  if (titleRoute) {
    document.title = isPWA ? (titleRoute.meta.title as string) : `${titleRoute.meta.title as string} - MeDaPro Everywhere`;
  } else {
    document.title = "MeDaPro Everywhere";
  }
}

document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "visible") {
    updateTitle();
  }
});

const checkLicense = (location: RouteLocationNormalizedGeneric, route: RouteRecordNormalized) => {
  if (store.state.user.username === "support") return { generalLicense: true, hasUserAssignedLicense: true };

  const application: number | null | undefined = typeof route.meta.application === "function" ? route.meta.application(location) : route.meta.application;
  if (!application) return;

  const licenses: StoredLicenses | undefined = store.state.licenses;
  const licenseName =
    typeof route.meta.licenseName === "function"
      ? (route.meta.licenseName as (route: RouteLocation) => LicenseName | LicenseName[] | undefined)(location)
      : (route.meta.licenseName as LicenseName | LicenseName[] | undefined);

  let generalLicense: boolean | undefined;
  if (!Array.isArray(licenseName))
    generalLicense = licenses && licenseName ? licenses[licenseName] !== "NotLicensed" && licenses[licenseName] !== "0" : undefined;
  else {
    for (const name of licenseName) {
      generalLicense = !!generalLicense || (licenses[name] !== "NotLicensed" && licenses[name] !== "0");
    }
  }
  const hasUserAssignedLicense = store.state.user.applicationLicenses?.[application];

  return { generalLicense, hasUserAssignedLicense };
};
export default router;
